/**
 * Frontend PK curve helpers.
 * Keep fallback timing semantics aligned with backend `pk_curve_gen.py`.
 */

/**
 * Calculate simulation dose count so the curve covers TDM samples and enough future time.
 */
export function calcSimDoses(intervalH: number, maxTdmTime = 0): number {
  if (intervalH <= 0) return 6;
  if (maxTdmTime > 0) return Math.max(6, Math.ceil(maxTdmTime / intervalH) + 1);
  return Math.max(6, Math.ceil(72 / intervalH));
}

export interface SimulationData {
  time: number[];
  concentration: number[];
  concentration_p025?: number[];
  concentration_p975?: number[];
  concentration_p05?: number[];
  concentration_p95?: number[];
  concentration_mean?: number[];
  concentration_mc_median?: number[];
  n_subjects?: number;
}

interface InitialRegimenLike {
  dose?: unknown;
  dose_mg?: unknown;
  interval?: unknown;
  interval_h?: unknown;
  infusion_h?: unknown;
  infusion_duration_h?: unknown;
  is_recommended?: unknown;
}

interface InitialResultLike {
  recommended_dose_mg?: unknown;
  dosing_interval_h?: unknown;
  infusion_duration_h?: unknown;
}

const finiteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export function regimenMatchesInitialResultCurve(
  regimen: InitialRegimenLike,
  result: InitialResultLike,
  isOralMode: boolean,
): boolean {
  const regimenDose = finiteNumber(regimen.dose ?? regimen.dose_mg);
  const regimenInterval = finiteNumber(regimen.interval ?? regimen.interval_h);
  const resultDose = finiteNumber(result.recommended_dose_mg);
  const resultInterval = finiteNumber(result.dosing_interval_h);
  if (regimenDose == null || regimenInterval == null || resultDose == null || resultInterval == null) {
    return false;
  }
  const regimenInfusion = isOralMode
    ? 0
    : finiteNumber(regimen.infusion_h ?? regimen.infusion_duration_h ?? result.infusion_duration_h);
  const resultInfusion = isOralMode ? 0 : finiteNumber(result.infusion_duration_h);
  return Math.abs(regimenDose - resultDose) <= 0.001
    && Math.abs(regimenInterval - resultInterval) <= 0.001
    && regimenInfusion != null
    && resultInfusion != null
    && Math.abs(regimenInfusion - resultInfusion) <= 0.001;
}

export function findInitialResultRegimenIndex(
  regimens: InitialRegimenLike[],
  result: InitialResultLike,
  isOralMode: boolean,
): number {
  const explicitlyRecommended = regimens.findIndex((regimen) => regimen.is_recommended === true);
  if (explicitlyRecommended >= 0) return explicitlyRecommended;
  return regimens.findIndex((regimen) => regimenMatchesInitialResultCurve(regimen, result, isOralMode));
}

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

export function hasSimulationPredictionBand(value: unknown): boolean {
  const data = asRecord(value);
  if (!data) return false;
  const time = data.time;
  if (!Array.isArray(time) || time.length === 0) return false;
  const lower = data.concentration_p025 ?? data.concentrationP025 ?? data.concentration_p05 ?? data.concentrationP05;
  const upper = data.concentration_p975 ?? data.concentrationP975 ?? data.concentration_p95 ?? data.concentrationP95;
  return Array.isArray(lower)
    && Array.isArray(upper)
    && lower.length === time.length
    && upper.length === time.length;
}

export function hasCombinedCurvePredictionBand(value: unknown): boolean {
  const curve = asRecord(value);
  if (!curve) return false;
  const optimized = curve.optimized_phase ?? curve.optimizedPhase;
  return hasSimulationPredictionBand(optimized);
}

export interface TdmSample {
  concentration?: number;
  time_after_dose?: number;
  dose?: number;
  infusion_duration?: number;
  dosing_interval?: number;
  n_doses?: number;
  regimen_index?: number;
  absolute_time_h?: number;
  label?: string;
}

export interface RawRegimenLike {
  dose?: number;
  dose_mg?: number;
  dosing_interval?: number;
  interval_h?: number;
  infusion_duration?: number;
  infusion_h?: number;
  n_doses?: number;
  n_doses_manual?: boolean;
  n_doses_source?: 'auto' | 'manual' | string;
  start_time_h?: number;
  dosing_start_time?: string;
}

const EPSILON = 1e-9;
const DEFAULT_OBSERVATION_EXTENSION_H = 24;

export interface CombinedCurveOptions {
  observationExtensionH?: number;
  observedThroughH?: number;
  concentrationScale?: number;
}

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const getDoseCountBeforeSwitch = (startH: number, nextStartH: number, intervalH: number): number => {
  if (intervalH <= 0) return 1;
  const diffH = nextStartH - startH;
  if (diffH <= 0) return 1;
  return Math.max(1, Math.floor(Math.max(diffH - EPSILON, 0) / intervalH) + 1);
};

const getDoseCountUntilTime = (startH: number, targetH: number, intervalH: number): number => {
  if (intervalH <= 0) return 1;
  const diffH = targetH - startH;
  if (diffH <= 0) return 1;
  return Math.max(1, Math.floor(diffH / intervalH) + 1);
};

const getRegimenStartMs = (regimen: RawRegimenLike): number | null => {
  if (!regimen?.dosing_start_time) return null;
  const value = new Date(regimen.dosing_start_time).getTime();
  return Number.isFinite(value) ? value : null;
};

const getHistoryStartOffsets = (regimenHistory?: RawRegimenLike[] | null): Record<number, number> => {
  const offsets: Record<number, number> = {};
  if (!regimenHistory?.length) return offsets;

  const hasExplicitStart = regimenHistory.some((regimen) => Number.isFinite(toFiniteNumber(regimen?.start_time_h, Number.NaN)));
  if (hasExplicitStart) {
    regimenHistory.forEach((regimen, index) => {
      const startH = toFiniteNumber(regimen?.start_time_h, Number.NaN);
      if (Number.isFinite(startH)) offsets[index] = startH;
    });
    return offsets;
  }

  const startTimes = regimenHistory
    .map((regimen) => getRegimenStartMs(regimen))
    .filter((value): value is number => value !== null)
    .sort((a, b) => a - b);
  if (!startTimes.length) return offsets;

  const firstStartMs = startTimes[0];
  regimenHistory.forEach((regimen, index) => {
    const startMs = getRegimenStartMs(regimen);
    if (startMs !== null) {
      offsets[index] = Math.max(0, (startMs - firstStartMs) / 3600000);
    }
  });
  return offsets;
};

export function getAbsoluteTdmTime(
  sample: TdmSample,
  regimenStartOffsets: Record<number, number> = {},
): number {
  const absoluteTime = toFiniteNumber(sample?.absolute_time_h, Number.NaN);
  if (Number.isFinite(absoluteTime)) return absoluteTime;

  const regimenIndex = Math.trunc(toFiniteNumber(sample?.regimen_index, -1));
  const intervalH = toFiniteNumber(sample?.dosing_interval, 12);
  const nDoses = Math.max(1, Math.trunc(toFiniteNumber(sample?.n_doses, 1)));
  const timeAfterDose = toFiniteNumber(sample?.time_after_dose, 0);
  const regimenStartH = regimenIndex >= 0 && Number.isFinite(regimenStartOffsets[regimenIndex])
    ? regimenStartOffsets[regimenIndex]
    : 0;

  return regimenStartH + (nDoses - 1) * intervalH + timeAfterDose;
}

export function buildTdmPoints(
  tdmData?: TdmSample[] | null,
  regimenHistory?: RawRegimenLike[] | null,
): { time: number; concentration: number; label?: string }[] {
  if (!tdmData?.length) return [];

  const regimenStartOffsets = getHistoryStartOffsets(regimenHistory);
  return tdmData
    .filter((sample) => toFiniteNumber(sample?.concentration, 0) > 0)
    .map((sample, index) => ({
      time: +getAbsoluteTdmTime(sample, regimenStartOffsets).toFixed(2),
      concentration: +toFiniteNumber(sample?.concentration, 0).toFixed(4),
      label: sample?.label || `TDM #${index + 1}`,
    }));
}

/**
 * Convert TDM data into chart points.
 */
export function tdmDataToPoints(
  tdmData?: TdmSample[] | null,
  patientData?: Record<string, any> | null,
  calcType?: string,
  regimenHistory?: RawRegimenLike[] | null,
): { time: number; concentration: number; label?: string }[] {
  if (tdmData && Array.isArray(tdmData) && tdmData.length > 0) {
    return buildTdmPoints(tdmData, regimenHistory);
  }

  const points: { time: number; concentration: number; label?: string }[] = [];

  if (calcType === 'pk_formula' && patientData?.pk_formula_input) {
    const input = patientData.pk_formula_input;
    const infDur = Number(input.infusion_duration);
    const interval = Number(input.dosing_interval);
    if (!Number.isFinite(infDur) || infDur <= 0 || !Number.isFinite(interval) || interval <= 0) {
      return points;
    }
    if (input.measured_trough != null && input.measured_trough > 0) {
      const troughTime = interval - (input.t_trough ?? 0);
      points.push({ time: +troughTime.toFixed(2), concentration: input.measured_trough, label: '实测谷浓度' });
    }
    if (input.measured_peak != null && input.measured_peak > 0) {
      const peakTime = interval + infDur + (input.t_peak ?? 0);
      points.push({ time: +peakTime.toFixed(2), concentration: input.measured_peak, label: '实测峰浓度' });
    }
  }

  return points;
}

/**
 * Monte Carlo PTA for combined AUC + Cmin target.
 */
export function computePtaMc(
  doseMg: number, intervalH: number, cl: number, vc: number,
  omegaCl: number, omegaVc: number, infusionH: number, nSim: number,
  targetMin: number, targetMax: number, cminMax: number,
): number {
  if (doseMg <= 0 || cl <= 0 || vc <= 0 || intervalH <= 0) return 0;
  let seed = 42;
  const rand = () => { seed |= 0; seed = (seed + 0x6D2B79F5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const gauss = (sd: number) => { const u1 = rand() || 1e-10; const u2 = rand(); return sd * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2); };
  const sdCl = Math.sqrt(omegaCl);
  const sdVc = Math.sqrt(omegaVc);
  const dpd = 24.0 / intervalH;
  let count = 0;
  for (let i = 0; i < nSim; i++) {
    const clI = cl * Math.exp(gauss(sdCl));
    const vcI = vc * Math.exp(gauss(sdVc));
    const keI = clI / vcI;
    const auc24 = (doseMg * dpd) / clI;
    let cmin = 0;
    if (keI > 0 && infusionH > 0) {
      const acc = 1.0 / (1.0 - Math.exp(-keI * intervalH));
      const cmax = (doseMg / (vcI * keI * infusionH)) * (1 - Math.exp(-keI * infusionH)) * acc;
      cmin = cmax * Math.exp(-keI * (intervalH - infusionH));
    }
    if (auc24 >= targetMin && auc24 <= targetMax && cmin <= cminMax) count++;
  }
  return Math.round((count / nSim) * 1000) / 10;
}

/**
 * Steady-state trough concentration for 1-compartment IV infusion.
 */
export function computePredictedCmin(
  doseMg: number, intervalH: number, ke: number, vc: number, infusionH: number,
): number {
  if (ke <= 0 || vc <= 0 || doseMg <= 0 || infusionH <= 0) return 0;
  const acc = 1.0 / (1.0 - Math.exp(-ke * intervalH));
  const cpeak = (doseMg / (vc * ke * infusionH)) * (1 - Math.exp(-ke * infusionH)) * acc;
  return +(cpeak * Math.exp(-ke * (intervalH - infusionH))).toFixed(2);
}

export interface FtMicResult {
  value: number;
  css_peak: number;
  css_trough: number;
  free_css_peak: number;
  free_css_trough: number;
  time_above_mic_h: number;
  mic: number;
  fu: number;
}

export interface ModelBsvLike {
  omega_cl?: number | null;
  omega_vc?: number | null;
  omega_cl_vc?: number | null;
  omega_ke?: number | null;
}

export function ftAboveMicCore(
  ke: number, vc: number, doseMg: number,
  intervalH: number, infusionH: number,
  mic: number, fu: number,
): FtMicResult {
  const zero: FtMicResult = { value: 0, css_peak: 0, css_trough: 0, free_css_peak: 0, free_css_trough: 0, time_above_mic_h: 0, mic, fu };
  if (ke <= 0 || vc <= 0 || doseMg <= 0 || intervalH <= 0 || infusionH <= 0) return zero;

  const tau = intervalH;
  const tinf = Math.min(infusionH, tau);
  const rate = doseMg / tinf;
  const accumulation = 1.0 / (1.0 - Math.exp(-ke * tau));
  const cssPeak = (rate / (ke * vc)) * (1.0 - Math.exp(-ke * tinf)) * accumulation;
  const cssTrough = cssPeak * Math.exp(-ke * (tau - tinf));
  const fPeak = fu * cssPeak;
  const fTrough = fu * cssTrough;

  const base = { css_peak: +cssPeak.toFixed(3), css_trough: +cssTrough.toFixed(3), free_css_peak: +fPeak.toFixed(3), free_css_trough: +fTrough.toFixed(3), mic, fu };

  if (mic <= 0) return { ...base, value: 100, time_above_mic_h: +tau.toFixed(2) };
  if (fTrough >= mic) return { ...base, value: 100, time_above_mic_h: +tau.toFixed(2) };
  if (fPeak <= mic) return { ...base, value: 0, time_above_mic_h: 0 };

  const tDown = tinf + Math.log(fPeak / mic) / ke;
  let tUp = 0;
  if (fTrough < mic) {
    const aVal = rate / (ke * vc);
    const bVal = cssTrough;
    const numerator = fu * aVal - mic;
    const denominator = fu * (aVal - bVal);
    if (denominator > 0 && numerator > 0) {
      const ratio = numerator / denominator;
      if (ratio > 0) {
        tUp = Math.max(0, Math.min(-Math.log(ratio) / ke, tinf));
      }
    }
  }
  const timeAbove = Math.max(0, Math.min(tDown - tUp, tau));
  return { ...base, value: +((timeAbove / tau) * 100).toFixed(1), time_above_mic_h: +timeAbove.toFixed(2) };
}

export function computeFtMicPta(
  ke: number, vc: number, doseMg: number,
  intervalH: number, infusionH: number,
  mic: number, fu: number, targetFt: number,
  omegaKe: number | null | undefined, omegaVc: number | null | undefined, nSim = 500,
): number | null {
  if (
    ke <= 0
    || vc <= 0
    || doseMg <= 0
    || typeof omegaKe !== 'number'
    || !Number.isFinite(omegaKe)
    || omegaKe < 0
    || typeof omegaVc !== 'number'
    || !Number.isFinite(omegaVc)
    || omegaVc < 0
  ) return null;
  let seed = 42;
  const rand = () => { seed |= 0; seed = (seed + 0x6D2B79F5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  const gauss = (sd: number) => { const u1 = rand() || 1e-10; const u2 = rand(); return sd * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2); };
  const sdKe = Math.sqrt(omegaKe);
  const sdVc = Math.sqrt(omegaVc);
  let nMet = 0;
  for (let i = 0; i < nSim; i++) {
    const keI = ke * Math.exp(gauss(sdKe));
    const vcI = vc * Math.exp(gauss(sdVc));
    if (keI <= 0 || vcI <= 0) continue;
    const r = ftAboveMicCore(keI, vcI, doseMg, intervalH, infusionH, mic, fu);
    if (r.value >= targetFt) nMet++;
  }
  return +((nMet / nSim) * 100).toFixed(1);
}

export function recomputeFtMicResults(
  ke: number, vc: number, doseMg: number, intervalH: number, infusionH: number,
  originalPkpd: any, drugConfig?: any, modelBsv?: ModelBsvLike,
): any {
  if (!originalPkpd) return originalPkpd;
  const details = originalPkpd.details || {};
  const mic = details.default_mic ?? drugConfig?.default_mic;
  const fu = details.fu ?? drugConfig?.default_fu;
  const targetFt = originalPkpd.target_min;
  const ptaTarget = details.pta_target;
  const omegaKe = modelBsv?.omega_ke;
  const omegaVc = modelBsv?.omega_vc;
  const nSim = drugConfig?.simulation_settings?.pta_monte_carlo_n
    ?? drugConfig?.pta_n_sim
    ?? 2000;
  const defaultResult = ftAboveMicCore(ke, vc, doseMg, intervalH, infusionH, mic, fu);

  if (
    !(typeof mic === 'number' && Number.isFinite(mic) && mic > 0)
    || !(typeof fu === 'number' && Number.isFinite(fu) && fu > 0 && fu <= 1)
    || !(typeof targetFt === 'number' && Number.isFinite(targetFt) && targetFt > 0)
    || !(typeof omegaKe === 'number' && Number.isFinite(omegaKe) && omegaKe >= 0)
    || !(typeof omegaVc === 'number' && Number.isFinite(omegaVc) && omegaVc >= 0)
  ) {
    return {
      ...originalPkpd,
      value: defaultResult.value,
      pta: null,
      primary_target_met: defaultResult.value >= targetFt,
      pta_met: null,
      pta_status: 'not_applicable',
      target_met_raw: defaultResult.value >= targetFt,
      target_met: defaultResult.value >= targetFt,
      details: {
        ...details,
        default_result: defaultResult,
        recalculation_status: 'unavailable_missing_model_bsv',
      },
    };
  }

  const defaultPta = computeFtMicPta(ke, vc, doseMg, intervalH, infusionH, mic, fu, targetFt, omegaKe, omegaVc, nSim);

  const breakpoints = details.mic_breakpoints?.map((bp: any) => bp.mic) ?? drugConfig?.mic_breakpoints ?? [mic];
  const micResults = breakpoints.map((bpMic: number) => {
    const r = ftAboveMicCore(ke, vc, doseMg, intervalH, infusionH, bpMic, fu);
    const pta = computeFtMicPta(ke, vc, doseMg, intervalH, infusionH, bpMic, fu, targetFt, omegaKe, omegaVc, nSim);
    return {
      mic: bpMic,
      ft_above_mic: r.value,
      pta,
      ft_met: r.value >= targetFt,
      pta_met: typeof ptaTarget === 'number' && Number.isFinite(ptaTarget)
        ? (typeof pta === 'number' ? pta >= ptaTarget : null)
        : null,
    };
  });

  const primaryTargetMet = defaultResult.value >= targetFt;
  const ptaMet = typeof ptaTarget === 'number' && Number.isFinite(ptaTarget)
    ? (typeof defaultPta === 'number' ? defaultPta >= ptaTarget : null)
    : null;
  const targetMet = primaryTargetMet && ptaMet !== false;

  return {
    ...originalPkpd,
    value: defaultResult.value,
    pta: defaultPta,
    primary_target_met: primaryTargetMet,
    pta_met: ptaMet,
    pta_status: ptaMet == null ? 'not_configured' : ptaMet ? 'met' : 'not_met',
    target_met_raw: targetMet,
    target_met: targetMet,
    details: {
      ...details,
      default_result: defaultResult,
      mic_breakpoints: micResults,
      primary_target_met: primaryTargetMet,
      pta_met: ptaMet,
      pta_status: ptaMet == null ? 'not_configured' : ptaMet ? 'met' : 'not_met',
      target_met_raw: targetMet,
    },
  };
}

export function generatePKCurve(
  ke: number,
  vc: number,
  doseMg: number,
  intervalH: number,
  infusionH: number,
  nDoses: number = 6,
  dt: number = 0.1,
): SimulationData {
  if (ke <= 0 || vc <= 0 || doseMg <= 0) {
    return { time: [], concentration: [] };
  }

  const rate = doseMg / infusionH;
  const totalTime = intervalH * nDoses + intervalH;
  const nPoints = Math.floor(totalTime / dt) + 1;

  const time: number[] = [];
  const concentration: number[] = [];

  for (let i = 0; i < nPoints; i++) {
    const t = +(i * dt).toFixed(2);
    let c = 0;

    for (let d = 0; d < nDoses; d++) {
      const tDoseStart = d * intervalH;
      const tSinceDose = t - tDoseStart;
      if (tSinceDose < 0) continue;

      if (tSinceDose <= infusionH) {
        c += (rate / (ke * vc)) * (1 - Math.exp(-ke * tSinceDose));
      } else {
        const cEnd = (rate / (ke * vc)) * (1 - Math.exp(-ke * infusionH));
        c += cEnd * Math.exp(-ke * (tSinceDose - infusionH));
      }
    }

    time.push(t);
    concentration.push(+c.toFixed(4));
  }

  return { time, concentration };
}

/** Generate one steady-state interval for the Sanford two-point display. */
export function generateSteadyStatePKCurve(
  ke: number,
  vc: number,
  doseMg: number,
  intervalH: number,
  infusionH: number,
  dt = 0.1,
): SimulationData {
  if (
    ke <= 0 || vc <= 0 || doseMg <= 0 || intervalH <= 0
    || infusionH <= 0 || infusionH >= intervalH || dt <= 0
  ) {
    return { time: [], concentration: [] };
  }

  const rate = doseMg / infusionH;
  const accumulation = 1 / (1 - Math.exp(-ke * intervalH));
  const peakSs = (rate / (ke * vc)) * (1 - Math.exp(-ke * infusionH)) * accumulation;
  const troughSs = peakSs * Math.exp(-ke * (intervalH - infusionH));
  const nPoints = Math.round(intervalH / dt) + 1;
  const time: number[] = [];
  const concentration: number[] = [];
  for (let index = 0; index < nPoints; index += 1) {
    const t = Math.min(index * dt, intervalH);
    const value = t <= infusionH
      ? troughSs * Math.exp(-ke * t) + (rate / (ke * vc)) * (1 - Math.exp(-ke * t))
      : peakSs * Math.exp(-ke * (t - infusionH));
    time.push(Number(t.toFixed(2)));
    concentration.push(Number(value.toFixed(4)));
  }
  if (time[time.length - 1] !== intervalH) {
    time.push(Number(intervalH.toFixed(2)));
    concentration.push(Number(troughSs.toFixed(4)));
  }
  return { time, concentration };
}

/** Generate a repeated steady-state profile for the meropenem formula view. */
export function generateSteadyStatePKCurveIntervals(
  ke: number,
  vc: number,
  doseMg: number,
  intervalH: number,
  infusionH: number,
  nIntervals = 2,
  dt = 0.1,
): SimulationData {
  if (nIntervals <= 1) {
    return generateSteadyStatePKCurve(ke, vc, doseMg, intervalH, infusionH, dt);
  }
  if (
    ke <= 0 || vc <= 0 || doseMg <= 0 || intervalH <= 0
    || infusionH <= 0 || infusionH >= intervalH || dt <= 0
  ) {
    return { time: [], concentration: [] };
  }

  const rate = doseMg / infusionH;
  const accumulation = 1 / (1 - Math.exp(-ke * intervalH));
  const peakSs = (rate / (ke * vc)) * (1 - Math.exp(-ke * infusionH)) * accumulation;
  const troughSs = peakSs * Math.exp(-ke * (intervalH - infusionH));
  const totalTime = intervalH * Math.trunc(nIntervals);
  const nPoints = Math.round(totalTime / dt) + 1;
  const time: number[] = [];
  const concentration: number[] = [];

  for (let index = 0; index < nPoints; index += 1) {
    const t = Math.min(index * dt, totalTime);
    const intervalIndex = t < totalTime ? Math.floor(t / intervalH) : Math.trunc(nIntervals);
    let localT = t - intervalIndex * intervalH;
    if (Math.abs(localT) < 1e-9 || intervalIndex >= Math.trunc(nIntervals)) localT = 0;
    const value = localT <= infusionH
      ? troughSs * Math.exp(-ke * localT) + (rate / (ke * vc)) * (1 - Math.exp(-ke * localT))
      : peakSs * Math.exp(-ke * (localT - infusionH));
    time.push(Number(t.toFixed(2)));
    concentration.push(Number(value.toFixed(4)));
  }

  if (time[time.length - 1] !== Number(totalTime.toFixed(2))) {
    time.push(Number(totalTime.toFixed(2)));
    concentration.push(Number(troughSs.toFixed(4)));
  }
  return { time, concentration };
}

export interface CombinedCurveData {
  currentPhase: SimulationData;
  optimizedPhase: SimulationData;
  continuationPhase: SimulationData;
  switchTimeH: number;
}

export interface DosingSegment {
  index?: number;
  doseMg: number;
  intervalH: number;
  infusionH: number;
  nDoses: number;
  startTimeH: number;
}

interface DoseEvent {
  timeH: number;
  doseMg: number;
  infusionH: number;
}

export function normalizeDosingSegments(
  regimens?: RawRegimenLike[] | null,
  tdmData?: TdmSample[] | null,
): DosingSegment[] {
  if (!regimens?.length) return [];

  const parsed = regimens.map((raw, index) => ({
    index,
    doseMg: toFiniteNumber(raw?.dose_mg ?? raw?.dose, 0),
    intervalH: toFiniteNumber(raw?.interval_h ?? raw?.dosing_interval, 12),
    infusionH: Math.max(toFiniteNumber(raw?.infusion_h ?? raw?.infusion_duration, 1), 0.1),
    nDoses: Math.max(0, Math.trunc(toFiniteNumber(raw?.n_doses, 0))),
    manual: raw?.n_doses_manual === true || String(raw?.n_doses_source || '').trim().toLowerCase() === 'manual',
    startTimeH: Number.isFinite(toFiniteNumber(raw?.start_time_h, Number.NaN)) ? toFiniteNumber(raw?.start_time_h, 0) : null,
    startMs: getRegimenStartMs(raw),
  }));

  parsed.sort((a, b) => {
    if (a.startMs === null && b.startMs === null) return a.index - b.index;
    if (a.startMs === null) return 1;
    if (b.startMs === null) return -1;
    return a.startMs - b.startMs || a.index - b.index;
  });

  const firstStartMs = parsed
    .map((regimen) => regimen.startMs)
    .find((value): value is number => value !== null);
  const latestTdmAbs = Math.max(0, ...(tdmData || []).map((sample) => getAbsoluteTdmTime(sample)));

  const normalized: DosingSegment[] = [];
  parsed.forEach((regimen, idx) => {
    if (regimen.doseMg <= 0 || regimen.intervalH <= 0) return;

    let startTimeH = regimen.startTimeH;
    if (startTimeH === null && regimen.startMs !== null && firstStartMs !== undefined) {
      startTimeH = Math.max(0, (regimen.startMs - firstStartMs) / 3600000);
    }
    if (startTimeH === null) {
      const prev = normalized[normalized.length - 1];
      startTimeH = prev ? prev.startTimeH + prev.nDoses * prev.intervalH : 0;
    }

    let nDoses = regimen.nDoses;
    if (nDoses <= 0 || !regimen.manual) {
      if (idx < parsed.length - 1) {
        const next = parsed[idx + 1];
        let nextStartH: number;
        if (next.startTimeH !== null) {
          nextStartH = next.startTimeH;
        } else if (next.startMs !== null && firstStartMs !== undefined) {
          nextStartH = Math.max(0, (next.startMs - firstStartMs) / 3600000);
        } else {
          nextStartH = startTimeH + regimen.intervalH;
        }
        nDoses = getDoseCountBeforeSwitch(startTimeH, nextStartH, regimen.intervalH);
      } else {
        nDoses = getDoseCountUntilTime(startTimeH, latestTdmAbs, regimen.intervalH);
      }
    }

    normalized.push({
      index: regimen.index,
      doseMg: regimen.doseMg,
      intervalH: regimen.intervalH,
      infusionH: regimen.infusionH,
      nDoses: Math.max(1, nDoses),
      startTimeH: +startTimeH.toFixed(4),
    });
  });

  return normalized;
}

export function buildDoseEvents(segments?: DosingSegment[] | null): DoseEvent[] {
  const events: DoseEvent[] = [];
  (segments || []).forEach((segment) => {
    if (segment.doseMg <= 0 || segment.intervalH <= 0) return;
    for (let doseIndex = 0; doseIndex < Math.max(1, segment.nDoses); doseIndex += 1) {
      events.push({
        timeH: +(segment.startTimeH + doseIndex * segment.intervalH).toFixed(4),
        doseMg: segment.doseMg,
        infusionH: segment.infusionH,
      });
    }
  });
  events.sort((a, b) => a.timeH - b.timeH);
  return events;
}

export function generateCombinedPKCurveMulti(
  ke: number, vc: number,
  segments: DosingSegment[],
  optDoseMg: number, optIntervalH: number, optInfusionH: number, optNDoses: number,
  dt: number = 0.1,
  options: CombinedCurveOptions = {},
): CombinedCurveData {
  const empty: SimulationData = { time: [], concentration: [] };
  if (ke <= 0 || vc <= 0 || segments.length === 0) {
    return { currentPhase: empty, optimizedPhase: empty, continuationPhase: empty, switchTimeH: 0 };
  }

  const orderedSegments = [...segments]
    .filter((segment) => segment.doseMg > 0 && segment.intervalH > 0)
    .sort((a, b) => a.startTimeH - b.startTimeH || (a.index ?? 0) - (b.index ?? 0));
  if (!orderedSegments.length) {
    return { currentPhase: empty, optimizedPhase: empty, continuationPhase: empty, switchTimeH: 0 };
  }

  const currentEvents = buildDoseEvents(orderedSegments);
  const lastSeg = orderedSegments[orderedSegments.length - 1];
  const lastCurrentDoseH = currentEvents.length > 0
    ? currentEvents[currentEvents.length - 1].timeH
    : lastSeg.startTimeH;
  const switchH = currentEvents.length > 0
    ? lastCurrentDoseH + lastSeg.intervalH
    : lastSeg.startTimeH + lastSeg.nDoses * lastSeg.intervalH;

  const optCount = Math.max(Math.trunc(toFiniteNumber(optNDoses, 1)), 1);
  const safeOptIntervalH = Math.max(toFiniteNumber(optIntervalH, 0), EPSILON);
  const safeOptInfusionH = Math.max(toFiniteNumber(optInfusionH, 1), 0.1);
  const concentrationScale = Math.max(toFiniteNumber(options.concentrationScale, 1), 0);
  const observedThroughH = toFiniteNumber(options.observedThroughH, Number.NaN);
  const hasObservedThrough = Number.isFinite(observedThroughH) && observedThroughH > 0;
  const observationExtensionH = Math.max(
    toFiniteNumber(options.observationExtensionH, DEFAULT_OBSERVATION_EXTENSION_H),
    0,
  );
  const fallbackLastOptimizedDoseH = switchH + (optCount - 1) * safeOptIntervalH;
  const observationAnchorH = hasObservedThrough
    ? Math.max(switchH, observedThroughH)
    : Math.max(switchH, lastCurrentDoseH, fallbackLastOptimizedDoseH);
  const totalTime = observationAnchorH + observationExtensionH;
  const nPoints = Math.floor(totalTime / dt) + 1;

  const contIntervalH = lastSeg.intervalH;
  const contNDoses = contIntervalH > 0
    ? Math.max(1, Math.floor(Math.max(totalTime - switchH, 0) / contIntervalH) + 1)
    : Math.max(lastSeg.nDoses, 1);
  const optimizedEvents: DoseEvent[] = [];
  if (optDoseMg > 0 && safeOptIntervalH > 0) {
    for (let d = 0; d < optCount; d += 1) {
      const timeH = +(switchH + d * safeOptIntervalH).toFixed(4);
      if (timeH <= totalTime + EPSILON) {
        optimizedEvents.push({ timeH, doseMg: optDoseMg, infusionH: safeOptInfusionH });
      }
    }
  }

  const curT: number[] = [];
  const curC: number[] = [];
  const optT: number[] = [];
  const optC: number[] = [];
  const contT: number[] = [];
  const contC: number[] = [];

  const doseContrib = (ts: number, doseMg: number, infH: number): number => {
    if (ts < 0 || doseMg <= 0 || infH <= 0) return 0;
    const rate = doseMg / infH;
    if (ts <= infH) {
      return (rate / (ke * vc)) * (1 - Math.exp(-ke * ts));
    }
    const cEnd = (rate / (ke * vc)) * (1 - Math.exp(-ke * infH));
    return cEnd * Math.exp(-ke * (ts - infH));
  };

  for (let i = 0; i < nPoints; i++) {
    const t = +(i * dt).toFixed(2);

    let cCurrent = 0;
    for (const event of currentEvents) {
      cCurrent += doseContrib(t - event.timeH, event.doseMg, event.infusionH);
    }

    let cOpt = 0;
    for (const event of optimizedEvents) {
      cOpt += doseContrib(t - event.timeH, event.doseMg, event.infusionH);
    }

    let cCont = cCurrent;
    for (let d = 0; d < contNDoses; d += 1) {
      cCont += doseContrib(t - (switchH + d * contIntervalH), lastSeg.doseMg, lastSeg.infusionH);
    }

    if (t <= switchH) {
      curT.push(t);
      curC.push(+(cCurrent * concentrationScale).toFixed(4));
    }
    if (t >= switchH) {
      optT.push(t);
      optC.push(+((cCurrent + cOpt) * concentrationScale).toFixed(4));
      contT.push(t);
      contC.push(+(cCont * concentrationScale).toFixed(4));
    }
  }

  return {
    currentPhase: { time: curT, concentration: curC },
    optimizedPhase: { time: optT, concentration: optC },
    continuationPhase: { time: contT, concentration: contC },
    switchTimeH: +switchH.toFixed(4),
  };
}

export function generateCombinedPKCurve(
  ke: number, vc: number,
  currentDoseMg: number, currentIntervalH: number, currentInfusionH: number, currentNDoses: number,
  optDoseMg: number, optIntervalH: number, optInfusionH: number, optNDoses: number,
  dt: number = 0.1,
  options: CombinedCurveOptions = {},
): CombinedCurveData {
  return generateCombinedPKCurveMulti(
    ke,
    vc,
    [{
      doseMg: currentDoseMg,
      intervalH: currentIntervalH,
      infusionH: currentInfusionH,
      nDoses: currentNDoses,
      startTimeH: 0,
    }],
    optDoseMg,
    optIntervalH,
    optInfusionH,
    optNDoses,
    dt,
    options,
  );
}
